/** Заставка-пар: первое, что человек видит, открыв витрину.
 *
 * Густой вейповый пар затягивает экран, вьётся к центру и вверх, а под ним уже
 * отрисован каталог — когда пар развеивается, витрина не «появляется», она там
 * была. CSS-блобами такой плотности не добиться: её даёт наложение десятков
 * полупрозрачных частиц, поэтому здесь canvas.
 *
 * Заставка держится не короче ~1.5 с — ровно чтобы клуб успел подняться и
 * осесть у имени. Это заставка первого открытия, не путь к заказу: диплинк из
 * чата (человек шёл в конкретный раздел) и reduced-motion её пропускают.
 */
import { useEffect, useRef, useState } from 'react';

/** Частица — один клуб: положение, скорость, радиус и остаток жизни. */
interface Puff {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  gr: number;
  amax: number;
  life: number;
  ttl: number;
  seed: number;
  sway: number;
}

const reduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Запускает частицы на холсте и отдаёт две ручки: развеять и остановить. */
function startSmoke(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  let width = 0;
  let height = 0;
  const size = () => {
    width = canvas.width = Math.round(window.innerWidth * dpr);
    height = canvas.height = Math.round(window.innerHeight * dpr);
  };
  size();
  window.addEventListener('resize', size);

  // Спрайт клуба рисуется один раз, дальше только drawImage —
  // это в разы дешевле, чем blur-фильтр на каждом кадре
  const S = 160;
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = S;
  const sg = sprite.getContext('2d');
  if (sg) {
    const grad = sg.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    grad.addColorStop(0, 'rgba(236,245,250,.6)');
    grad.addColorStop(0.45, 'rgba(233,242,247,.3)');
    grad.addColorStop(1, 'rgba(233,242,247,0)');
    sg.fillStyle = grad;
    sg.fillRect(0, 0, S, S);
  }

  const rnd = (a: number, b: number) => a + Math.random() * (b - a);
  const puffs: Puff[] = [];

  function spawn(prefill: boolean) {
    // Клубы рождаются по всей ширине у дна экрана и поднимаются вверх,
    // раздуваясь. Плюс очаг в центре — он закрывает логотип, пока грузится
    // каталог, и марка проступает сквозь пар, а не поверх пустоты.
    const center = Math.random() < 0.3;
    const base = center
      ? {
          x: rnd(0.32, 0.68) * width,
          y: rnd(0.32, 0.68) * height,
          vx: rnd(-0.3, 0.3) * dpr,
          vy: -rnd(0.3, 0.8) * dpr,
          r: rnd(0.09, 0.15) * width,
        }
      : {
          x: rnd(-0.08, 1.08) * width,
          y: rnd(1.0, 1.3) * height,
          vx: rnd(-0.95, 0.35) * dpr, // лёгкий боковой снос
          vy: -rnd(2.65, 9.5) * dpr, // главный вектор — вверх
          r: rnd(0.11, 0.18) * width,
        };

    const ttl = rnd(170, 190); // цикл короче — дым живее
    puffs.push({
      ...base,
      gr: rnd(0.16, 0.88) * dpr, // клуб раздувается быстрее
      amax: rnd(0.2, 0.9),
      life: prefill ? rnd(0, ttl * 0.15) : 0, // экран густой с первого кадра
      ttl,
      seed: rnd(0, Math.PI * 2),
      sway: rnd(0.8, 0.7) * dpr,
    });
  }

  for (let i = 0; i < 90; i++) spawn(true);

  let raf = 0;
  let dispersing = false;

  function frame() {
    ctx!.clearRect(0, 0, width, height);
    if (!dispersing && puffs.length < 150) for (let i = 0; i < 4; i++) spawn(false);

    for (let i = puffs.length - 1; i >= 0; i--) {
      const p = puffs[i]!;
      p.life++;
      if (dispersing) {
        p.vy -= 0.55 * dpr;
        p.ttl = Math.min(p.ttl, p.life + 45);
      }
      const t = p.life / p.ttl;
      if (t >= 1) {
        puffs.splice(i, 1);
        continue;
      }
      p.x += p.vx + Math.sin(p.life / 38 + p.seed) * p.sway;
      p.y += p.vy;
      p.r += p.gr;
      ctx!.globalAlpha = p.amax * Math.min(1, p.life / 45) * Math.pow(1 - t, 1.25);
      ctx!.drawImage(sprite, p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
    }
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    disperse() {
      dispersing = true;
    },
    stop() {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
    },
  };
}

/**
 * Заставка живёт, пока `ready` не станет true, но не меньше своего минимума.
 * Возвращает null, когда всё уже растворилось — тогда её просто нет в дереве.
 */
export function SmokeLoader({
  ready,
  onClearing,
}: {
  ready: boolean;
  /** Пар начал развеиваться.
   *
   * Именно этот момент, а не «пар исчез»: развеивание длится почти секунду, и
   * то, что ждало своей очереди, должно встать под паром заранее — тогда он
   * рассеивается уже над готовым экраном. Иначе между чистой витриной и
   * появлением следующего слоя зияет пауза, и кажется, что приложение
   * задумалось.
   */
  onClearing?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<ReturnType<typeof startSmoke>>(null);
  const startedAt = useRef(Date.now());
  const [phase, setPhase] = useState<'show' | 'clearing' | 'gone'>('show');
  // Колбэк живёт в ref: он приходит стрелкой из JSX и менялся бы каждый
  // рендер, перезапуская таймер развеивания — заставка не кончалась бы никогда
  const done = useRef(onClearing);
  done.current = onClearing;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduced()) return;
    engineRef.current = startSmoke(canvas);
    return () => engineRef.current?.stop();
  }, []);

  useEffect(() => {
    if (!ready || phase !== 'show') return;

    // Диплинк из чата — человек шёл заказывать конкретный раздел, держать его
    // паром нельзя; reduced-motion смотрит на статичный логотип — тоже незачем
    const instant =
      new URLSearchParams(location.search).has('list') || reduced();
    const wait = instant ? 0 : Math.max(0, 1500 - (Date.now() - startedAt.current));

    const begin = setTimeout(() => {
      setPhase('clearing');
      engineRef.current?.disperse();
      done.current?.();
    }, wait);
    return () => clearTimeout(begin);
  }, [ready, phase]);

  useEffect(() => {
    if (phase !== 'clearing') return;
    const end = setTimeout(() => {
      setPhase('gone');
      engineRef.current?.stop();
      engineRef.current = null;
    }, 950);
    return () => clearTimeout(end);
  }, [phase]);

  if (phase === 'gone') return null;

  return (
    <div className={`loader ${phase === 'clearing' ? 'clearing' : ''}`}>
      <canvas ref={canvasRef} aria-hidden />
      <div className="mark">ParBet</div>
    </div>
  );
}
